import React from 'react';
import { useRouter } from 'next/router';
import { typography, typographyColors, spacing } from '@/design-system/utils';
import { safeRouterPush } from '@/utils/safeRouterPush';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

const ChevronRightIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 20,
  color = '#4C4C4C',
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 20 20'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M8 5L13 10L8 15'
      stroke={color}
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const router = useRouter();

  const handleClick = (href?: string) => {
    if (href) {
      void safeRouterPush(router, href);
    }
  };

  return (
    <div
      className='px-4 md:px-14'
      style={{
        display: 'flex',
        gap: '14px',
        alignItems: 'center',
        paddingTop: spacing[6],
        paddingBottom: spacing[2],
      }}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isClickable = item.href && !isLast;

        return (
          <React.Fragment key={index}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isLast ? spacing[2] : spacing[1.5],
                borderRadius: isLast ? '59px' : '41px',
                cursor: isClickable ? 'pointer' : 'default',
              }}
              onClick={() => isClickable && handleClick(item.href)}
            >
              <p
                style={{
                  fontFamily: typography.fonts.primary,
                  fontSize: typography.size.sm, // 14px
                  fontWeight: isLast
                    ? typography.weight.bold // 700
                    : typography.weight.regular, // 400
                  color: isLast
                    ? typographyColors.primary // #1C1917
                    : '#4C4C4C', // Gray for links
                  lineHeight: 'normal',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                {item.label}
              </p>
            </div>
            {!isLast && <ChevronRightIcon size={20} color='#4C4C4C' />}
          </React.Fragment>
        );
      })}
    </div>
  );
};
